import type {
	GraphNode,
	NodePosition,
	PositionsMap,
	LayoutResult
} from '../types';

// Fixed layout using the official OSRS wiki coordinates
// This replicates the exact positioning from tools.runescape.wiki

interface Bounds {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
}

export function computeFixedLayout(nodes: Record<string, GraphNode>): LayoutResult {
	const positions: PositionsMap = {};
	
	// Calculate bounds from all node coordinates
	let bounds: Bounds = {
		minX: Infinity,
		maxX: -Infinity,
		minY: Infinity,
		maxY: -Infinity
	};
	
	// First pass: collect positions and calculate bounds
	Object.values(nodes).forEach(node => {
		const coord = node.coord;
		if (!coord) {
			console.warn(`Node ${node.id} missing coord data`);
			return;
		}
		
		// Use the raw coordinates from the wiki data
		const x = coord.x;
		const y = coord.y;
		
		positions[node.id] = {
			x,
			y,
			node
		};
		
		// Update bounds
		bounds.minX = Math.min(bounds.minX, x);
		bounds.maxX = Math.max(bounds.maxX, x);
		bounds.minY = Math.min(bounds.minY, y);
		bounds.maxY = Math.max(bounds.maxY, y);
	});
	
	// Calculate dimensions
	const padding = 80; // Padding around the layout
	const totalWidth = (bounds.maxX - bounds.minX) + (padding * 2);
	const totalHeight = (bounds.maxY - bounds.minY) + (padding * 2);
	
	// Adjust positions to be centered and have positive coordinates
	const offsetX = padding - bounds.minX;
	const offsetY = padding - bounds.minY;
	
	// Second pass: normalize positions
	Object.keys(positions).forEach(nodeId => {
		const pos = positions[nodeId];
		pos.x += offsetX;
		pos.y += offsetY;
	});
	
	// Create level information based on Y positions
	const levelY: Record<number, number> = {};
	const sortedY = [...new Set(Object.values(positions).map(p => p.y))].sort((a, b) => a - b);
	sortedY.forEach((y, index) => {
		levelY[index] = y;
	});
	
	return {
		positions,
		zoneCenters: {}, // Not used in fixed layout
		zoneWidths: {},
		levelY,
		maxLevel: sortedY.length - 1,
		totalWidth,
		totalHeight
	};
}
